import './index.css';
const QA = {
    largeTitle: 'navigation-bar-container navigation-bar-container_large',
    standardTitle: 'navigation-bar-container navigation-bar-container_standard',
    items: 'navigation-bar-items',
};
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const animate = (element, ...keyframes) => element.animate(keyframes, { duration: 1000, fill: 'forwards' });
/**
 * Функция с помощью [WAAPI](https://developer.mozilla.org/en-US/docs/Web/API/Web_Animations_API) инициализирует
 * анимации на всех элементах NavigationBar к которым они должны быть применены в процессе скролла.
 * Кроме того вычисляет и инициализирует метаданные для этих анимаций которые используются в
 * дальнейшем при их применении.
 *
 * @param state Объект храняший внутреннее состояние NavigationBar
 * @param backgroundContainerRef ref на элемент отображающий бэкграунд позади всех частей NavigationBar
 * @param beforeScrollTitleRef ref на элемент содержащий заголовок до скролла (для size = large это будет элемент
 * содержаший весь большой заголовок, для size = standard это будет элемент содержащий текст заголовка до скролла)
 * @param afterScrollTitleRef ref на элемент содержащий заголовок после скролла (для size = large это будет элемент
 * содержаший весь standard заголовок, для size = standard это будет элемент содержащий текст заголовка после скролла)
 * @param optionsRef ref на элемент содержащий блок options
 * @param largeItemsRef ref на элемент содержащий значение пропсов right и left в заголовке large размера
 * @param standardItemsRef ref на элемент содержащий значение пропсов right и left в заголовке standard размера
 * @param dividerRef ref на дивайдер
 */
const initAnimations = (state, backgroundContainerRef, beforeScrollTitleRef, afterScrollTitleRef, optionsRef, largeItemsRef, standardItemsRef, dividerRef) => {
    state.animations.forEach(({ animation }) => animation.cancel());
    const animations = [];
    const { allowOptionsScroll, sizes, size } = state;
    let totalOffset = 0;
    const blurOptions = (prev, current) => {
        if (prev <= 0.3 && current > 0.3) {
            allowOptionsScroll &&
                document.activeElement &&
                optionsRef.current?.contains(document.activeElement) &&
                document.activeElement?.blur?.();
        }
    };
    if (size === 'large' && beforeScrollTitleRef.current && afterScrollTitleRef.current && largeItemsRef.current) {
        const titleTranslationDelta = sizes.largeTitle - sizes.standardTitle + sizes.itemsContainer;
        totalOffset = titleTranslationDelta + (allowOptionsScroll ? sizes.optionsContainer : 0);
        if (optionsRef.current) {
            const optionsTranslationDelta = allowOptionsScroll ? sizes.optionsContainer : titleTranslationDelta;
            animations.push({
                durationInPixels: optionsTranslationDelta,
                startOffsetInPixels: 0,
                animation: animate(optionsRef.current, { transform: 'translateY(0)' }, { transform: `translateY(-${optionsTranslationDelta}px)` }),
                groupName: allowOptionsScroll ? 'optionsAnimation' : 'largeTitleAnimation',
                onProgressCallback: blurOptions,
            });
        }
        const offset = allowOptionsScroll ? sizes.optionsContainer : 0;
        animations.push({
            durationInPixels: titleTranslationDelta,
            startOffsetInPixels: offset,
            animation: animate(beforeScrollTitleRef.current, { transform: 'translateY(0)', opacity: 1, pointerEvents: 'auto' }, { transform: `translateY(-${titleTranslationDelta}px)`, opacity: 0, pointerEvents: 'none' }),
            onFinishCallback: () => beforeScrollTitleRef.current && (beforeScrollTitleRef.current.dataset.qa = ''),
            onStartCallback: () => beforeScrollTitleRef.current && (beforeScrollTitleRef.current.dataset.qa = QA.largeTitle),
            groupName: 'largeTitleAnimation',
        }, {
            durationInPixels: sizes.itemsContainer,
            startOffsetInPixels: offset,
            animation: animate(largeItemsRef.current, { opacity: 1, pointerEvents: 'auto' }, { opacity: 0, pointerEvents: 'none' }),
            onFinishCallback: () => {
                largeItemsRef.current && (largeItemsRef.current.dataset.qa = '');
                standardItemsRef.current && (standardItemsRef.current.dataset.qa = QA.items);
            },
            onStartCallback: () => {
                largeItemsRef.current && (largeItemsRef.current.dataset.qa = QA.items);
                standardItemsRef.current && (standardItemsRef.current.dataset.qa = '');
            },
            groupName: 'largeTitleAnimation',
        }, {
            durationInPixels: sizes.itemsContainer,
            startOffsetInPixels: offset,
            animation: animate(afterScrollTitleRef.current, { opacity: 0, transform: `translateY(${sizes.itemsContainer / 2}px)`, pointerEvents: 'none' }, { opacity: 1, transform: 'translateY(0)', pointerEvents: 'auto' }),
            onFinishCallback: () => afterScrollTitleRef.current && (afterScrollTitleRef.current.dataset.qa = QA.standardTitle),
            onStartCallback: () => afterScrollTitleRef.current && (afterScrollTitleRef.current.dataset.qa = ''),
            groupName: 'largeTitleAnimation',
        });
    }
    else if (size === 'standard' && beforeScrollTitleRef.current && afterScrollTitleRef.current) {
        const hasOptionsScroll = optionsRef.current && allowOptionsScroll;
        if (hasOptionsScroll) {
            animations.push({
                durationInPixels: sizes.optionsContainer,
                startOffsetInPixels: 0,
                animation: animate(optionsRef.current, { transform: 'translateY(0)' }, { transform: 'translateY(-100%)' }),
                groupName: 'optionsAnimation',
                onProgressCallback: blurOptions,
            });
            totalOffset = sizes.optionsContainer;
        }
        animations.push({
            // 40 тут просто случайное число, чтобы у анимации всегда была какая-то минимальная продолжительность
            durationInPixels: hasOptionsScroll ? sizes.optionsContainer : 40,
            startOffsetInPixels: 0,
            animation: animate(beforeScrollTitleRef.current, { opacity: 1 }, { opacity: 0 }),
            groupName: 'standardTitleAnimation',
        }, {
            // 40 тут просто случайное число, чтобы у анимации всегда была какая-то минимальная продолжительность
            durationInPixels: hasOptionsScroll ? sizes.optionsContainer : 40,
            startOffsetInPixels: 0,
            animation: animate(afterScrollTitleRef.current, { opacity: 0 }, { opacity: 1 }),
            groupName: 'standardTitleAnimation',
        });
    }
    // Эффект может состоять из нескольких анимаций, например эффект трансформации заголовка из размера large
    // в размер standard состоит из подъема большого заголовка, подьема блока опций, смены прозрачности верхнего блока
    // Каждая из частей эффекта может длиться разное число пикселей. При этом если общее количество пикселей доступных
    // для скролла меньше чем необходимо для проигрывания эффекта до конца, то мы не хотим запускать эффект вообще.
    // Чтобы реализовать эту логику анимации разбиваются на группы по признаку использования в определенном эффекте,
    // для каждой группы вычисляется минимальное количество пикселей скролла необходимое для проигрывания всех анимаций
    // группы до конца. Далее при проигрывании анимации мы будем игнорировать анимации относящиеся к эффектам для
    // которых требуется больше пикселей, чем доступно в скролле
    const animationGroupsLength = animations.reduce((map, animation) => {
        if (!animation.groupName) {
            return map;
        }
        map[animation.groupName] = Math.max(map[animation.groupName] ?? 0, animation.startOffsetInPixels + animation.durationInPixels);
        return map;
    }, {});
    animations.forEach((animation) => {
        // помечаем анимации которые сдвигают элементы
        animation.isTranslation = animation.animation.effect
            ?.getKeyframes?.()
            .some((keyframe) => !!keyframe.transform);
        if (!animation.groupName) {
            animation.minScrollToPlay = animation.startOffsetInPixels + animation.durationInPixels;
            return;
        }
        animation.minScrollToPlay = animationGroupsLength[animation.groupName];
    });
    if (totalOffset > 0) {
        const animationStops = [animationGroupsLength.largeTitleAnimation, animationGroupsLength.optionsAnimation]
            .filter(Boolean)
            // сортируем в порядке убывания чисел, так будет чуть проще писать цикл обхода по ним
            .sort((a, b) => b - a);
        // Поднимаем дивайдер и бэкграунд на нужное число пикселей
        // Т.к. подниматься они могут из-за двух анимаций - поднятие большого заголовка и блока с опциями,
        // и при этом каждая из анимаций может быть проигнорирована из-за недостаточного количества пикселей
        // доступных для скролла, задаем minScrollToPlay в виде массива
        // При проверке этого параметра в функции применения анимации если это массив мы находим в нем наибольшее число,
        // которое меньше количества доступных для скролла пикселей, и при достижении скроллом этого значения
        // останавливаем прогресс анимации.
        dividerRef.current &&
            animations.push({
                durationInPixels: totalOffset,
                startOffsetInPixels: 0,
                animation: animate(dividerRef.current, { transform: `translateY(0)` }, { transform: `translateY(-${totalOffset}px)` }),
                minScrollToPlay: animationStops.length === 1 ? animationStops[0] : animationStops,
            });
        backgroundContainerRef.current &&
            animations.push({
                durationInPixels: totalOffset,
                startOffsetInPixels: 0,
                animation: animate(backgroundContainerRef.current, { transform: `translateY(0)` }, { transform: `translateY(-${totalOffset}px)` }),
                minScrollToPlay: animationStops.length === 1 ? animationStops[0] : animationStops,
            });
    }
    animations.forEach(({ animation }) => animation.pause());
    state.animations = animations;
};
/**
 * Функция вычисляет и применяет currentTime к анимациям на основе значения скролла и scrollGroundPos (вычисляется
 * функцией updateScrollGroundPos). При необходимости вызывает коллбеки начала/конца анимации.
 * Кроме того вычисляет прогресс анимации (animationProgress) и прогресс перемещения (transitionProgress)
 *
 * @param state Объект храняший внутреннее состояние NavigationBar
 * @param forceRunCallbacks Флаг включающий обязательное применение onStart/onFinish коллбеков анимаций. По умолчанию
 * эти коллбеки применяются только в моменты достижения анимацией своего начала или конца. При передаче этого флага
 * коллбек будет вызван при каждом вызове этой функции, а какой конкретно - будет зависеть от прогресса анимации,
 * больше или меньше 50%
 */
const applyAnimations = (state, forceRunCallbacks = false) => {
    const { animations, scrollGroundPos, currentScrollTop, maxScrollTop } = state;
    const relativeScroll = currentScrollTop - scrollGroundPos;
    state.animationProgress = 1;
    state.transitionProgress = 0;
    for (const animation of animations) {
        let maxScrollToPlay = 0;
        if (Array.isArray(animation.minScrollToPlay)) {
            // находим максимальное значение, которое меньше или равно maxScrollTop
            // именно до этой точки можно проиграть анимацию чтобы она выглядела законченной
            for (let i = 0; i < animation.minScrollToPlay.length; i++) {
                if (animation.minScrollToPlay[i] <= maxScrollTop) {
                    maxScrollToPlay = animation.minScrollToPlay[i];
                    break;
                }
            }
        }
        else {
            maxScrollToPlay = (animation.minScrollToPlay || 0) > maxScrollTop ? 0 : animation.minScrollToPlay || 0;
        }
        // анимацию нужно применить в любом случае, иначе при смене пропсов компонент может визуально сломаться
        const appliedScroll = Math.min(relativeScroll, maxScrollToPlay);
        const animationProgress = clamp((appliedScroll - animation.startOffsetInPixels) / animation.durationInPixels, 0, 1);
        const prevProgress = animation.currentProgress || 0;
        if ((prevProgress < 1 && animationProgress >= 1 && animation.onFinishCallback) ||
            (animationProgress >= 0.5 && forceRunCallbacks)) {
            void Promise.resolve().then(animation.onFinishCallback);
        }
        else if ((prevProgress > 0 && animationProgress <= 0 && animation.onStartCallback) ||
            (animationProgress < 0.5 && forceRunCallbacks)) {
            void Promise.resolve().then(animation.onStartCallback);
        }
        if (animation.onProgressCallback) {
            const prevProgress = animation.currentProgress || 0;
            void Promise.resolve().then(() => animation.onProgressCallback?.(prevProgress, animationProgress));
        }
        animation.currentProgress = animationProgress;
        animation.animation.currentTime = animationProgress * 1000;
        state.animationProgress = Math.min(relativeScroll / Math.max(maxScrollToPlay, 0.1) /* не делим на 0 */, state.animationProgress);
        if (animation.isTranslation) {
            state.transitionProgress = Math.max(state.transitionProgress, appliedScroll);
        }
    }
};

export { QA, applyAnimations, initAnimations };
//# sourceMappingURL=animationUtils.js.map
